import { Loading, Typography } from "@cpj-ai/panel-npm-package";
import { useGetEmployeeStatisticsQuery } from "@graphql/generated";
import { Card } from "@heroui/react";
import SemicircleProgress from "@modules/company/components/SemiCircleProgress";
import { useParams } from "react-router-dom";

interface StatusItemProps {
  color: string;
  label: string;
  count: number;
}

const StatusItem = ({ color, label, count }: StatusItemProps) => (
  <div className="flex justify-between items-center gap-2 pr-8">
    <div className="flex items-center gap-2">
      <div className={`w-2 h-2 ${color} rounded-sm`} />
      <Typography className="text-sm">{label}:</Typography>
    </div>
    <Typography className="text-sm">{count}</Typography>
  </div>
);

interface LocationStatsProps {
  label: string;
  count: number;
  total: number;
  isLarge?: boolean;
}

const LocationStats = ({ label, count, total, isLarge = false }: LocationStatsProps) => {
  const percentage = ((count || 0) / (total || 1)) * 100;
  const TypographyComponent = isLarge ? Typography : Typography;
  const countClassName = isLarge ? "!text-2xl font-bold" : "text-lg";
  const containerClassName = isLarge ? "flex flex-col gap-2" : "flex flex-col gap-2 w-fit";

  return (
    <div className={containerClassName}>
      <Typography className={isLarge ? "text-zinc-500" : "text-zinc-500 text-sm"}>{label}:</Typography>
      <TypographyComponent className={countClassName}>
        {count || 0} <span className="text-zinc-400 text-sm">{percentage.toFixed(1)}%</span>
      </TypographyComponent>
    </div>
  );
};

const styles = {
  card: "py-5 px-5 grid grid-cols-12 gap-4 mb-9 shadow-sm border-1 border-zinc-100 rounded-lg",
  statsContainer: "col-span-3 gap-4",
  statusContainer: "col-span-3 border-l border-[#E8E8E8] px-5 border-r flex flex-col gap-3",
  locationContainer: "col-span-6 flex flex-col gap-7",
  locationRow: "flex gap-8",
} as const;

const statusItems = [
  { color: "bg-green-600", label: "Active", key: "active" },
  { color: "bg-yellow-400", label: "Suspended", key: "suspended" },
  { color: "bg-red-600", label: "Ended", key: "finished" },
  { color: "bg-cyan-700", label: "Awaiting start", key: "awaiting" },
  { color: "bg-red-500", label: "Fired", key: "fired" },
  { color: "bg-red-400", label: "Resigned", key: "resigned" },
] as const;

const locationItems = [
  { label: "In person", key: "limitedAreaCount" },
  { label: "Online", key: "hybridCount" },
  { label: "Hybrid", key: "hybridCount" },
  { label: "Remote", key: "remoteCount" },
  { label: "Global", key: "globalCount" },
  { label: "On-site", key: "preciseLocationCount" },
  { label: "On the road", key: "onTheRoadCount" },
] as const;

const EmployeesStatistics = () => {
  const { id } = useParams();
  const { data, loading } = useGetEmployeeStatisticsQuery({
    variables: {
      organizationId: id || "",
    },
  });

  if (loading) return <Loading />;

  const employeeData = data?.admin?.organizations?.edges[0]?.node;
  const statusCounts = employeeData?.employeeCooperationStatusCounts || {};
  const locationCounts = employeeData?.jobPositionLocationTypeCounts || {};

  const totalEmployees = Object.values(statusCounts).reduce((sum: number, count) => {
    const numCount = typeof count === "number" ? count : 0;
    return sum + numCount;
  }, 0);

  const activeEmployees = (statusCounts.active as number) || 0;

  return (
    <Card className={styles.card}>
      <div className={styles.statsContainer}>
        <Typography className="font-semibold text-sm text-[#202020]">Employee statistics :</Typography>
        <SemicircleProgress className="w-full mx-auto" currentPoints={activeEmployees} totalPoints={totalEmployees} />
        <Typography className="text-xs text-center text-[#484848]">
          {activeEmployees} active and working employees from {totalEmployees} hiring history
        </Typography>
      </div>

      <div className={styles.statusContainer}>
        {statusItems.map(({ color, label, key }) => (
          <StatusItem
            key={key}
            color={color}
            label={label}
            count={(statusCounts[key as keyof typeof statusCounts] as number) || 0}
          />
        ))}
      </div>

      <div className={styles.locationContainer}>
        <div className={styles.locationRow}>
          {locationItems.slice(0, 2).map(({ label, key }) => (
            <LocationStats
              key={key}
              label={label}
              count={(locationCounts[key as keyof typeof locationCounts] as number) || 0}
              total={totalEmployees}
              isLarge
            />
          ))}
        </div>
        <div className={styles.locationRow}>
          {locationItems.slice(2).map(({ label, key }) => (
            <LocationStats
              key={key}
              label={label}
              count={(locationCounts[key as keyof typeof locationCounts] as number) || 0}
              total={totalEmployees}
            />
          ))}
        </div>
      </div>
    </Card>
  );
};

export default EmployeesStatistics;
